import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Trash2, RefreshCcw, ExternalLink, MessageSquare } from "lucide-react";

const STATUS_BADGE = {
  new: { label: "Nouveau", color: "bg-amber-100 text-amber-800 border-amber-400" },
  in_progress: { label: "En cours", color: "bg-blue-100 text-blue-800 border-blue-400" },
  handled: { label: "Traité", color: "bg-emerald-100 text-emerald-800 border-emerald-400" },
  spam: { label: "Spam", color: "bg-red-100 text-red-800 border-red-400" },
};

const TYPE_LABEL = {
  bug: "🐛 Bug",
  sales: "💼 Commercial",
  gdpr: "🔒 RGPD",
  other: "❓ Autre",
};

export default function AdminContact() {
  const [data, setData] = useState({ items: [], total: 0, new_count: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    const { data: d } = await api.get("/admin/contact", { params });
    setData(d);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contact/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
      if (open?.id === id) setOpen({ ...open, status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce message définitivement ?")) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      toast.success("Supprimé");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admin-contact-page">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Contact</div>
          <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Messages reçus</h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {data.total} total · <span className="text-amber-700">{data.new_count} nouveaux</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm h-10 w-44" data-testid="contact-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-sm">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="new">Nouveaux</SelectItem>
              <SelectItem value="in_progress">En cours</SelectItem>
              <SelectItem value="handled">Traités</SelectItem>
              <SelectItem value="spam">Spam</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" className="rounded-sm gap-2" data-testid="contact-reload">
            <RefreshCcw className="h-4 w-4" /> Recharger
          </Button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-accent/30 font-mono text-[11px] uppercase text-muted-foreground">
            <tr>
              <th className="border-b border-border p-2">Date</th>
              <th className="border-b border-border p-2">Statut</th>
              <th className="border-b border-border p-2">Type</th>
              <th className="border-b border-border p-2">De</th>
              <th className="border-b border-border p-2">Message</th>
              <th className="border-b border-border p-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((m) => {
              const badge = STATUS_BADGE[m.status] || STATUS_BADGE.new;
              return (
                <tr key={m.id} className="border-b border-border last:border-0 cursor-pointer hover:bg-accent/40" onClick={() => setOpen(m)} data-testid={`contact-row-${m.id}`}>
                  <td className="p-2 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</td>
                  <td className="p-2"><span className={`border ${badge.color} px-2 py-0.5 font-mono text-[10px] uppercase`}>{badge.label}</span></td>
                  <td className="p-2 text-xs">{TYPE_LABEL[m.inquiry_type] || m.inquiry_type}</td>
                  <td className="p-2 text-xs"><div>{m.name || "—"}</div><div className="text-muted-foreground">{m.email}</div></td>
                  <td className="p-2 text-xs text-muted-foreground"><div className="max-w-md truncate">{m.message}</div></td>
                  <td className="p-2"><MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /></td>
                </tr>
              );
            })}
            {data.items.length === 0 && (
              <tr><td colSpan="6" className="p-12 text-center font-mono text-xs text-muted-foreground">Aucun message</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{open && (TYPE_LABEL[open.inquiry_type] || open.inquiry_type)} — {open?.name || open?.email}</DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <div><span className="text-muted-foreground">Email</span><div className="text-foreground">{open.email}</div></div>
                <div><span className="text-muted-foreground">Reçu le</span><div className="text-foreground">{new Date(open.created_at).toLocaleString()}</div></div>
                <div><span className="text-muted-foreground">IP</span><div className="text-foreground">{open.ip || "—"}</div></div>
                <div><span className="text-muted-foreground">Locale</span><div className="text-foreground">{open.locale}</div></div>
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase text-muted-foreground">Message</div>
                <Textarea readOnly value={open.message} rows={8} className="mt-1 rounded-sm font-mono text-xs bg-muted/40" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">Statut</span>
                  <Select value={open.status} onValueChange={(v) => updateStatus(open.id, v)}>
                    <SelectTrigger className="rounded-sm h-9 w-44" data-testid="contact-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-sm">
                      <SelectItem value="new">Nouveau</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="handled">Traité</SelectItem>
                      <SelectItem value="spam">Spam</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <a href={`mailto:${open.email}?subject=Re%3A%20Votre%20message%20%C3%A0%20SAVEBOX`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-sm gap-2"><Mail className="h-4 w-4" /> Répondre <ExternalLink className="h-3 w-3" /></Button>
                  </a>
                  <Button variant="outline" onClick={() => remove(open.id)} className="rounded-sm gap-2 text-destructive hover:text-destructive" data-testid="contact-delete">
                    <Trash2 className="h-4 w-4" /> Supprimer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
